import * as Sentry from '@sentry/browser';

const THIRD_PARTY_DOMAINS = [
  /ftUtils\.js$/i,
  /try\.abtasty\.com/,
  /script\.4dex\.io/,
  /pagead2\.googlesyndication\.com/,
  /googletagmanager\.com/,
  /securepubads\.g\.doubleclick\.net/,
  /stpd\.cloud/,
  /cdn\.mgaru\.dev/,
  /ident\.mygaru\.com/,
  /doubleclick\.net/,
  /google-analytics\.com/,
  /googlesyndication\.com/,
  /adservice\.google/,
  /symfony\/ux-live-component/,
  /ams\.creativecdn\.com/,
  /track\.adform\.net/,
];

const fileErrorExcludeList = [
  {
    filepath: '@ingatlancom/stimulus-gallery/src/controllers/gallery_controller.js',
    errorMessage: /Right side of assignment cannot be destructured/,
  },
  {
    filepath: '@ingatlancom/stimulus-gallery/src/controllers/gallery_controller.js',
    errorMessage: /Cannot read properties of undefined \(reading 'type'\)/,
  },
];

const isExcludedFileError = (errorText, frames) => {
  return fileErrorExcludeList.some((fileError) => {
    const filenameMatch = frames.some((f) => f.filename?.includes(fileError.filepath));
    const messageMatch = fileError.errorMessage.test(errorText);

    return filenameMatch && messageMatch;
  });
};

const isThirdPartyScript = (filename) => {
  return THIRD_PARTY_DOMAINS.some((pattern) => pattern.test(filename));
};

const getStackFrames = (event) => {
  if (!event.exception?.values) {
    return [];
  }

  return event.exception.values.flatMap((exception) => exception.stacktrace?.frames || []);
};

const isThirdPartyError = (frames) => {
  return frames.some((frame) => frame.filename && isThirdPartyScript(frame.filename));
};

const getErrorText = (event) => {
  const message = event.message || '';
  const exceptionMessage = event.exception?.values?.[0]?.value || '';

  return message + exceptionMessage;
};

const shouldDropEvent = (event) => {
  const frames = getStackFrames(event);

  // Drop errors from third-party scripts
  if (isThirdPartyError(frames)) {
    return true;
  }

  const errorText = getErrorText(event);

  if (isExcludedFileError(errorText, frames)) {
    return true;
  }

  // Drop common non-actionable errors
  // List of error patterns to ignore
  const ignorePatterns = [/ResizeObserver loop/i, /Non-Error promise rejection/i, /Load failed/i];

  return ignorePatterns.some((pattern) => pattern.test(errorText));
};

Sentry.init({
  dsn: document.body.dataset.sentryDsn,
  environment: document.body.dataset.sentryEnv,
  integrations: [Sentry.browserTracingIntegration()],
  // Set sample rate (0.0 to 1.0)
  // tracesSampleRate: 1.0, // Capture 100% of transactions for testing
  tracesSampleRate: 0.0005, // Capture 0.05% in production (to reduce quota usage)
  // Overwrite the defaults to ensure no trace headers are sent
  tracePropagationTargets: [],
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  debug: false,
  beforeSend(event) {
    if (shouldDropEvent(event)) {
      return null;
    }
    return event;
  },
  denyUrls: [...THIRD_PARTY_DOMAINS, 'img.hasznaltautocdn.com'],
  allowUrls: [/hasznaltauto(-test[1234])?\.hu/, /hasznaltautocdn(-test[1234])?\.com/, /hasznaltauto-local\.hu/],
  ignoreErrors: [
    /Blocked a restricted frame with origin (['"])?https:\/\/www\.hasznaltauto(?:-test[1234])?\.hu\1 from accessing another frame/,
    "undefined is not an object (evaluating 'window.webkit.messageHandlers')",
    /null is not an object \(evaluating '[a-z]\(\)\.document'\)/,
    'Failed to fetch (stpd.cloud)',
    'Invalid LatLng object: (NaN, NaN)',
    'Java object is gone',
    /^[a-z]\(\) is null$/,
    'undefined is not an object (evaluating window.webkit.messageHandlers[e].postMessage)',
    "Failed to read a named property 'stpd' from 'Window'",
    "TypeError: Cannot read properties of null (reading 'document')",
    /NetworkError when attempting to fetch resource\. \((stpd\.cloud|ident\.mygaru\.com)\)/,
    /Loading CSS chunk \d{3} failed/,
    "Failed to read the 'localStorage' property from 'Window': Access is denied for this document.",
  ],
});
